import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import useResponsive from '../hooks/useResponsive';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  message,
  DatePicker,
  Upload,
  Image,
  Row,
  Col,
  Card,
  Modal
} from 'antd';
import { UploadOutlined, SnippetsOutlined, ClearOutlined } from '@ant-design/icons';
// 移除路由相关导入
import { createAccount, updateAccount, getAccount, uploadAccountImage, uploadAccountImages, getAccountTypes, createEmailAccount, createEmailToken, getEmailList, deleteEmailAccount } from '../services/api';
import { getAccountImages } from '../services/api';

// 为单张图片添加随机元素来避免MD5重复检测
async function addRandomElementsToImage(file) {
  try {
    // 将文件转换为ImageBitmap
    const imageBitmap = await createImageBitmap(file);
    
    // 创建canvas
    const canvas = document.createElement('canvas');
    canvas.width = imageBitmap.width;
    canvas.height = imageBitmap.height;
    const ctx = canvas.getContext('2d');
    
    // 绘制原图
    ctx.drawImage(imageBitmap, 0, 0);
    
    // 添加随机元素
    const timestamp = Date.now();
    const randomSeed = Math.floor(Math.random() * 10000);
    
    // 在图片的多个角落添加微小的随机像素点
    const randomPixels = Math.floor(Math.random() * 5) + 3; // 3-7个随机像素
    for (let p = 0; p < randomPixels; p++) {
      const px = Math.floor(Math.random() * imageBitmap.width);
      const py = Math.floor(Math.random() * imageBitmap.height);
      const randomColor = `hsl(${Math.floor(Math.random() * 360)}, 0%, ${Math.floor(Math.random() * 10) + 95}%)`;
      ctx.fillStyle = randomColor;
      ctx.fillRect(px, py, 1, 1);
    }
    
    // 转换为Blob，保持高质量
    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, file.type || 'image/jpeg', 0.98);
    });
    
    // 创建新文件
    const originalName = file.name || 'image';
    const extension = originalName.split('.').pop() || 'jpg';
    const newFileName = `${originalName.split('.')[0]}_${timestamp}_${randomSeed}.${extension}`;
    
    return new File([blob], newFileName, { type: blob.type });
  } catch (error) {
    console.error('添加随机元素失败，使用原文件:', error);
    // 如果修改失败，返回原文件
    return file;
  }
}

// 将图片压缩到较小尺寸，保持高质量
async function resizeImageFile(inputFile, maxSize = 1200, quality = 0.92) {
  try {
    const imgBitmap = await createImageBitmap(inputFile);
    const ratio = Math.min(1, maxSize / Math.max(imgBitmap.width, imgBitmap.height));
    const targetW = Math.max(1, Math.round(imgBitmap.width * ratio));
    const targetH = Math.max(1, Math.round(imgBitmap.height * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgBitmap, 0, 0, targetW, targetH);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    return new File([blob], (inputFile.name || 'image') + '.jpg', { type: 'image/jpeg' });
  } catch (_) {
    return inputFile; // 失败则返回原文件
  }
}

const { Option } = Select;
const { TextArea } = Input;
const COUNTRY_OPTIONS = [
  '美国','英国','加拿大','澳大利亚','德国','法国','日本','韩国','新加坡','中国','其他'
];

const EMAIL_DOMAINS = ['dafs.top', 'ruohaotech.com'];
const EMAIL_DOMAIN_STORAGE_KEY = 'tk_email_domain_default';

// 与 services/api.js 保持一致的 API 基址推断（避免 pages.dev 下 /api 指向静态站）
const DEFAULT_WORKERS_API = 'https://api.tkerp.ruohaotech.com/api';
const isPagesHost = typeof window !== 'undefined' && /\.pages\.dev$/.test(window.location.hostname);
const isStaticPagesCustomDomain = typeof window !== 'undefined' && window.location.hostname === 'tkerp.ruohaotech.com';
const API_BASE_IMG = (typeof window !== 'undefined')
  ? (isPagesHost || isStaticPagesCustomDomain ? DEFAULT_WORKERS_API : `${window.location.origin}/api`)
  : '/api';

const AccountForm = ({ account, onSuccess, onCancel, accountId, defaultStatus }) => {
  const [form] = Form.useForm();
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(false);
  const [creatingEmail, setCreatingEmail] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState(false);
  const [emailDomain, setEmailDomain] = useState(() => {
    try {
      const saved = localStorage.getItem(EMAIL_DOMAIN_STORAGE_KEY);
      if (saved && EMAIL_DOMAINS.includes(saved)) return saved;
    } catch (_) {}
    return 'dafs.top';
  });
  const [imageUrl, setImageUrl] = useState(account?.image_url || null);
  const [imageUrls, setImageUrls] = useState(Array.isArray(account?.image_urls) ? account.image_urls : []);
  const [accountTypes, setAccountTypes] = useState([]);
  const [selectedImageBed, setSelectedImageBed] = useState('imgbb');
  const [pasteBuffer, setPasteBuffer] = useState([]); // { id, file, preview }
  const [pasteUploading, setPasteUploading] = useState(false);
  const fileInputRef = useRef(null);
  const pasteZoneRef = useRef(null);
  const pasteBufferRef = useRef([]);
  const isEdit = !!account || !!accountId;
  // 记录本次会话中创建的邮箱 slug，用于在关闭窗口时自动删除
  const createdEmailSlugRef = useRef(null);
  const toDayjsOrNull = (v) => {
    if (!v) return null;
    try {
      const d = dayjs(v);
      return d.isValid() ? d : null;
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    if (isEdit && accountId) {
      fetchAccount();
      // 拉取完整图片
      getAccountImages(accountId).then(res => {
        const imgs = Array.isArray(res?.image_urls) ? res.image_urls : [];
        setImageUrls(imgs);
        setImageUrl(res?.image_url || imgs[0] || null);
      }).catch(()=>{});
    } else if (account) {
      form.setFieldsValue({
        ...account,
        window_opened_at: toDayjsOrNull(account.window_opened_at)
      });
      // 初始化图片预览
      setImageUrl(account.image_url || null);
      // 若列表数据未带全量图片，则从接口补齐
      if (account?.id) {
        getAccountImages(account.id).then(res => {
          const imgs = Array.isArray(res?.image_urls) ? res.image_urls : [];
          setImageUrls(imgs);
          if (!account.image_url) setImageUrl(res?.image_url || imgs[0] || null);
        }).catch(()=>{
          // 回退到传入的 image_urls
        });
      }
      try {
        const imgs = Array.isArray(account.image_urls)
          ? account.image_urls
          : account.image_urls
            ? JSON.parse(account.image_urls)
            : [];
        setImageUrls(imgs || []);
      } catch (e) {
        const list = typeof account.image_urls === 'string'
          ? account.image_urls.split(',').map(s => s.trim()).filter(Boolean)
          : [];
        setImageUrls(list);
      }
    }
    fetchAccountTypes();

    // 初始化邮箱域名默认值（如果表单里还没有）
    try {
      const current = form.getFieldValue('email_domain');
      if (!current) form.setFieldsValue({ email_domain: emailDomain });
    } catch (_) {}
    
    // 组件卸载时，如果是在添加模式下创建了邮箱但未保存，自动删除邮箱
    return () => {
      if (!isEdit && createdEmailSlugRef.current) {
        // 异步删除，不阻塞组件卸载
        const slugToDelete = createdEmailSlugRef.current;
        deleteEmailAccount(slugToDelete).catch(err => {
          // 404 或 not_found 错误是正常的（邮箱可能已被删除或创建失败），静默处理
          const errorMsg = err?.message || err?.toString() || '';
          const isNotFound = errorMsg.includes('not_found') || 
                           errorMsg.includes('404') || 
                           errorMsg.includes('不存在');
          if (!isNotFound) {
            // 只有非 404 错误才记录警告
            console.warn('自动删除未保存的邮箱失败:', err);
          }
        });
      }
    };
  }, [accountId, account, isEdit]);

  const fetchAccountTypes = async () => {
    try {
      const types = await getAccountTypes();
      setAccountTypes(Array.isArray(types) ? types : []);
    } catch (error) {
      console.error('获取账号类型失败:', error);
    }
  };

  const fetchAccount = async () => {
    try {
      const data = await getAccount(accountId);
      form.setFieldsValue({
        ...data,
        window_opened_at: toDayjsOrNull(data.window_opened_at)
      });
      // 初始化图片预览
      setImageUrl(data.image_url || null);
      try {
        const imgs = Array.isArray(data.image_urls)
          ? data.image_urls
          : data.image_urls
            ? JSON.parse(data.image_urls)
            : [];
        setImageUrls(imgs || []);
      } catch (e) {
        const list = typeof data.image_urls === 'string'
          ? data.image_urls.split(',').map(s => s.trim()).filter(Boolean)
          : [];
        setImageUrls(list);
      }
    } catch (error) {
      message.error('获取账号信息失败');
    }
  };

  // 将 URL 或 Base64 加载为 HTMLImageElement
  const loadHtmlImage = (src) => new Promise((resolve, reject) => {
    try {
      const img = new Image();
      // 统一通过 Worker 代理，避免第三方无 CORS 污染 Canvas
      img.crossOrigin = 'anonymous';
      // 避免向图床携带 Referer 触发防盗链
      img.referrerPolicy = 'no-referrer';
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(new Error('图片加载失败'));
      const toProxied = (s) => {
        if (typeof s !== 'string') return null;
        if (/^data:/.test(s)) return s; // 本地 base64 直接用
        try {
          const fixed = s.replace(/^http:\/\//i, 'https://');
          const encoded = encodeURIComponent(fixed);
          return `${API_BASE_IMG}/proxy-image?url=${encoded}`;
        } catch (_) { return s; }
      };
      const finalSrc = toProxied(src);
      if (!finalSrc) return reject(new Error('非法图片来源'));
      // dataURL 直接使用，避免二次 fetch
      if (/^data:/.test(finalSrc)) {
        img.src = finalSrc;
        return;
      }
      // 以 JS 拉取为 blob，再转 objectURL，规避跨域标记
      fetch(finalSrc, { credentials: 'include' })
        .then(r => r.ok ? r.blob() : Promise.reject(new Error('代理获取失败')))
        .then(blob => {
          const objUrl = URL.createObjectURL(blob);
          img.src = objUrl;
        })
        .catch(err => reject(err));
    } catch (e) {
      reject(e);
    }
  });

  // 将一组 URL 预转换为 dataURL，避免不同域拼接导致报错
  const toDataUrls = async (sources) => {
    const out = [];
    for (const s of sources) {
      if (typeof s === 'string' && /^data:/.test(s)) { out.push(s); continue; }
      try {
        const encoded = encodeURIComponent(s);
        const resp = await fetch(`${API_BASE_IMG}/fetch-as-dataurl?url=${encoded}`, { credentials: 'include' });
        const json = await resp.json();
        if (json?.dataUrl) out.push(json.dataUrl); else out.push(s);
      } catch (_) {
        out.push(s);
      }
    }
    return out;
  };

  // 与本地 image_stitcher.py 对齐的拼接参数
  // 水平一排：max_width=1920, max_height=1080, spacing=10, JPEG quality=95, 拉伸填满格子
  const STITCH_OUT_W = 1920;
  const STITCH_OUT_H = 1080;
  const STITCH_SPACING = 10;
  const STITCH_JPEG_QUALITY = 0.95;

  /** 按 Python create_horizontal_stitch / create_custom_grid 逻辑拼接 ImageBitmap / HTMLImageElement */
  const stitchImagesLikePython = (imgs, mode = 'horizontal') => {
    if (!imgs || !imgs.length) return null;
    let rows = 1;
    let cols = imgs.length;
    if (mode === 'grid2') { rows = 2; cols = 2; }
    else if (mode === 'grid3') { rows = 3; cols = 3; }
    else {
      // horizontal：始终一排（含 4 张），与 Python 默认一致
      rows = 1;
      cols = imgs.length;
    }

    const spacing = STITCH_SPACING;
    const cellW = Math.floor((STITCH_OUT_W - (cols - 1) * spacing) / cols);
    const cellH = Math.floor((STITCH_OUT_H - (rows - 1) * spacing) / rows);
    const baseW = cellW * cols + spacing * (cols - 1);
    const baseH = cellH * rows + spacing * (rows - 1);

    // 与 Python 一致：输出精确像素尺寸（不做 DPR 放大，避免与脚本结果不一致）
    const canvas = document.createElement('canvas');
    canvas.width = baseW;
    canvas.height = baseH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, baseW, baseH);

    const limit = Math.min(imgs.length, rows * cols);
    for (let i = 0; i < limit; i++) {
      const r = Math.floor(i / cols);
      const c = i % cols;
      const x = c * (cellW + spacing);
      const y = r * (cellH + spacing);
      // 对应 Python Image.resize(..., LANCZOS) 后直接 paste：拉伸填满单元格
      ctx.drawImage(imgs[i], x, y, cellW, cellH);
    }

    return canvas.toDataURL('image/jpeg', STITCH_JPEG_QUALITY);
  };

  // 通用拼接：从指定 sources 生成拼图并返回 dataURL
  const stitchFromSources = async (sourcesRaw, mode = 'horizontal') => {
    try {
      if (!sourcesRaw || !sourcesRaw.length) {
        message.warning('请先上传图片');
        return null;
      }
      const sources = await toDataUrls(sourcesRaw);
      const settled = await Promise.allSettled(sources.map(loadHtmlImage));
      const imgs = settled.filter(x => x.status === 'fulfilled').map(x => x.value);
      if (!imgs.length) {
        message.error('拼图失败：图片全部加载失败');
        return null;
      }
      return stitchImagesLikePython(imgs, mode);
    } catch (e) {
      return null;
    }
  };

  // 生成拼图：默认 horizontal（与 Python 一致，多图一排）
  const stitchCurrentImages = async (mode = 'horizontal') => {
    const sourcesRaw = (imageUrls || []).slice(0, mode === 'horizontal' ? 10 : mode === 'grid2' ? 4 : 9);
    const dataUrl = await stitchFromSources(sourcesRaw, mode);
    if (!dataUrl) { message.error('生成拼图失败，请稍后重试'); return; }
    setImageUrls(prev => Array.from(new Set([dataUrl, ...(Array.isArray(prev) ? prev : [])])));
    setImageUrl(dataUrl);
    message.success('拼图已生成');
  };

  // 从本地选择文件并直接拼接（避免先上传成链接导致跨域）
  const handlePickAndStitch = (mode = 'horizontal') => {
    try {
      if (!fileInputRef.current) return;
      const inputEl = fileInputRef.current;
      const onChange = async (e) => {
        try {
          const files = Array.from(e.target.files || []).filter(f => /^image\//.test(f.type));
          if (!files.length) return;
          // 拼接前不预压缩，保留清晰度（与 Python 直接读原图一致）
          const limit = mode === 'horizontal' ? 10 : mode === 'grid2' ? 4 : 9;
          const pick = files.slice(0, limit);
          const bitmapsSettle = await Promise.allSettled(pick.map(f => createImageBitmap(f)));
          const bitmaps = bitmapsSettle.filter(x => x.status === 'fulfilled').map(x => x.value);
          if (!bitmaps.length) { message.error('本地拼图失败'); return; }
          const stitched = stitchImagesLikePython(bitmaps, mode);
          if (!stitched) { message.error('本地拼图失败'); return; }
          setImageUrls(prev => Array.from(new Set([stitched, ...(Array.isArray(prev) ? prev : [])])));
          setImageUrl(stitched);
          message.success('拼图已生成');
          inputEl.value = '';
        } catch (_) {
          message.error('本地拼图失败');
        } finally {
          inputEl.removeEventListener('change', onChange);
        }
      };
      inputEl.addEventListener('change', onChange);
      inputEl.click();
    } catch (_) {
      message.error('无法打开本地选择器');
    }
  };

  // 创建邮箱账号
  const handleCreateEmail = async () => {
    setCreatingEmail(true);
    try {
      // 获取用户名作为邮箱前缀
      const username = form.getFieldValue('username');
      if (!username) {
        message.warning('请先输入用户名，将使用用户名作为邮箱前缀');
        setCreatingEmail(false);
        return;
      }

      // 生成邮箱前缀：使用用户名前半段（最多10个字符），去除特殊字符，转小写
      let baseLocal = username.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!baseLocal) {
        message.error('用户名无效，无法生成邮箱前缀');
        setCreatingEmail(false);
        return;
      }

      // 限制长度，使用前半段（最多10个字符）
      if (baseLocal.length > 10) {
        baseLocal = baseLocal.substring(0, 10);
      }

      // 获取现有邮箱列表，检查是否重复
      let existingEmails = [];
      try {
        const emailListResult = await getEmailList();
        if (emailListResult.ok && emailListResult.list) {
          existingEmails = emailListResult.list.map(item => {
            const addr = item.addr || '';
            return addr.split('@')[0]; // 提取邮箱前缀
          });
        }
      } catch (e) {
        console.warn('获取邮箱列表失败，将尝试创建:', e);
      }

      // 生成不重复的邮箱前缀
      let local = baseLocal;
      let attempts = 0;
      const maxAttempts = 10;
      
      while (existingEmails.includes(local) && attempts < maxAttempts) {
        // 如果重复，添加随机后缀（1-3位数字）
        const randomSuffix = Math.floor(Math.random() * 1000).toString();
        local = baseLocal.substring(0, Math.max(1, 10 - randomSuffix.length)) + randomSuffix;
        attempts++;
      }

      // 如果还是重复，使用时间戳后缀
      if (existingEmails.includes(local)) {
        const timestamp = Date.now().toString().slice(-4);
        local = baseLocal.substring(0, Math.max(1, 10 - timestamp.length)) + timestamp;
      }

      const domain = form.getFieldValue('email_domain') || emailDomain || 'dafs.top';
      
      // 调用邮箱API创建邮箱
      const result = await createEmailAccount(local, domain);
      
      if (result.ok && result.addr) {
        // 自动填充邮箱地址
        form.setFieldsValue({ email: result.addr });
        
        // 使用公开链接（publicUrl）作为API，而不是token
        if (result.publicUrl) {
          let finalPublicUrl = result.publicUrl;
          // 根据选择的邮箱域名，动态切换展示用的邮件查询域名
          try {
            const match = result.publicUrl.match(/\/public\/([^/]+)\/mails/);
            if (match && match[1]) {
              const slug = match[1];
              // 选择 dafs.top 时，使用 email.dafs.top 域名
              if (domain === 'dafs.top') {
                finalPublicUrl = `https://email.dafs.top/public/${slug}/mails`;
              }
              // 记录 slug，用于在关闭窗口时自动删除（仅限添加模式）
              if (!isEdit) {
                createdEmailSlugRef.current = slug;
                console.log('已记录邮箱 slug 用于自动删除:', slug);
              }
            } else {
              console.warn('无法从 publicUrl 中提取 slug:', result.publicUrl);
            }
          } catch (e) {
            console.warn('处理 publicUrl 失败:', e);
          }

          form.setFieldsValue({ api_key: finalPublicUrl });
          message.success(`邮箱创建成功！邮箱：${result.addr}，API已自动填充`);
        } else {
          message.warning(`邮箱创建成功！邮箱：${result.addr}，但未获取到公开链接`);
          // 如果返回了 slug，也记录（备用方案）
          if (!isEdit && result.slug) {
            createdEmailSlugRef.current = result.slug;
            console.log('已记录邮箱 slug 用于自动删除（备用方案）:', result.slug);
          }
        }
      } else {
        message.error(result.error || '创建邮箱失败');
      }
    } catch (error) {
      console.error('创建邮箱失败:', error);
      const errorMsg = error.response?.data?.error || error.message || '创建邮箱失败';
      message.error(errorMsg);
    } finally {
      setCreatingEmail(false);
    }
  };

  // 生成随机密码
  const generateRandomPassword = () => {
    // 排除容易混淆的字母：l, L, i, I, o, O（数字0和1可以出现）
    // 可用的大写字母（排除 L, I, O）
    const upperLetters = 'ABCDEFGHJKMNPQRSTUVWXYZ';
    // 可用的小写字母（排除 l, i, o）
    const lowerLetters = 'abcdefghjkmnpqrstuvwxyz';
    
    // 确保至少包含一个大写和一个小写字母
    const nameLength = Math.floor(Math.random() * 3) + 3; // 3-5个字符
    
    // 首字母必须是大写（排除易混淆字符）
    let namePart = upperLetters[Math.floor(Math.random() * upperLetters.length)];
    
    // 确保至少有一个小写字母
    const hasLower = Math.random() > 0.3; // 70%概率在中间位置插入小写字母
    let lowerInserted = false;
    
    // 生成剩余字符
    for (let i = 1; i < nameLength; i++) {
      // 如果还没有小写字母，强制添加一个
      if (!lowerInserted && (i === nameLength - 1 || hasLower)) {
        namePart += lowerLetters[Math.floor(Math.random() * lowerLetters.length)];
        lowerInserted = true;
      } else {
        // 随机选择大写或小写字母
        const isUpper = Math.random() > 0.5;
        const charSet = isUpper ? upperLetters : lowerLetters;
        namePart += charSet[Math.floor(Math.random() * charSet.length)];
        if (!isUpper) lowerInserted = true;
      }
    }
    
    // 如果还是没有小写字母，强制在末尾添加一个
    if (!lowerInserted) {
      namePart += lowerLetters[Math.floor(Math.random() * lowerLetters.length)];
    }
    
    // 获取今天的日期（MMDD格式，如 1117 表示11月17日）
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // 01-12
    const day = String(today.getDate()).padStart(2, '0'); // 01-31
    const datePart = month + day; // 如 1117
    
    // 添加2个随机数字（0-9，包括0和1）
    const allDigits = '0123456789';
    const randomDigits = allDigits[Math.floor(Math.random() * allDigits.length)] + 
                         allDigits[Math.floor(Math.random() * allDigits.length)];
    
    // 随机选择 @ 或 .
    const specialChar = Math.random() > 0.5 ? '@' : '.';
    
    // 组合：Erin111788@ 格式（字母 + 日期MMDD + 随机数字 + 特殊字符）
    const password = namePart + datePart + randomDigits + specialChar;
    
    return password;
  };

  // 处理生成随机密码
  const handleGeneratePassword = () => {
    const randomPassword = generateRandomPassword();
    form.setFieldsValue({ password: randomPassword });
    message.success('随机密码已生成');
  };

  // 删除邮箱账号
  const handleDeleteEmail = async () => {
    const email = form.getFieldValue('email');
    const apiKey = form.getFieldValue('api_key');
    
    if (!email || !email.includes('@')) {
      message.warning('当前账号没有关联的邮箱');
      return;
    }
    const emailDomainPart = String(email.split('@')[1] || '').trim().toLowerCase();
    if (emailDomainPart && !EMAIL_DOMAINS.includes(emailDomainPart)) {
      message.warning(`当前邮箱域名不在临时邮箱系统范围内：${emailDomainPart}`);
      return;
    }

    // 从API字段（publicUrl）中提取slug
    let slug = null;
    if (apiKey && apiKey.includes('/public/')) {
      // 从 publicUrl 中提取 slug，格式：https://emailapi.ruohaotech.com/public/s_xxx/mails
      const match = apiKey.match(/\/public\/([^\/]+)/);
      if (match && match[1]) {
        slug = match[1];
      }
    }

    // 如果无法从API字段提取，尝试从邮箱列表查找
    if (!slug) {
      try {
        const emailListResult = await getEmailList();
        if (emailListResult.ok && emailListResult.list) {
          const emailInfo = emailListResult.list.find(item => item.addr === email);
          if (emailInfo && emailInfo.slug) {
            slug = emailInfo.slug;
          }
        }
      } catch (e) {
        console.warn('获取邮箱列表失败:', e);
      }
    }

    if (!slug) {
      message.error('无法找到邮箱的slug，请手动删除');
      return;
    }

    Modal.confirm({
      title: '确认删除邮箱',
      content: `确定要删除邮箱 ${email} 吗？此操作不可恢复！`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setDeletingEmail(true);
        try {
          const result = await deleteEmailAccount(slug);
          if (result.ok) {
            message.success('邮箱删除成功');
            // 清空邮箱和API字段
            form.setFieldsValue({ email: '', api_key: '' });
          } else {
            message.error(result.error || '删除邮箱失败');
          }
        } catch (error) {
          console.error('删除邮箱失败:', error);
          const errorMsg = error.response?.data?.error || error.message || '删除邮箱失败';
          message.error(errorMsg);
        } finally {
          setDeletingEmail(false);
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 格式化时间字段
      // 直接使用当前前端选中的图片作为最终结果，支持删除
      const mergedAll = Array.isArray(imageUrls) ? imageUrls : [];
      const payload = {
        ...values,
        price: values.price ?? 0,
        cost: values.cost ?? 0,
        image_url: mergedAll[0] || null,
        image_urls: mergedAll,
        window_opened_at: values.window_opened_at ? values.window_opened_at.format ? values.window_opened_at.format('YYYY-MM-DD') : values.window_opened_at : null,
        // 计算利润
        profit: (values.price || 0) - (values.cost || 0)
      };
      if (isEdit) {
        await updateAccount(account?.id || accountId, payload);
        message.success('账号更新成功');
      } else {
        await createAccount(payload);
        message.success('账号创建成功');
        // 账号创建成功，清除邮箱删除标记（邮箱已保存，不需要删除）
        createdEmailSlugRef.current = null;
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('表单提交失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMsg = error.response?.data?.error || error.message || (isEdit ? '更新失败' : '创建失败');
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const clearPasteBuffer = () => {
    setPasteBuffer((prev) => {
      prev.forEach((item) => {
        try { if (item.preview) URL.revokeObjectURL(item.preview); } catch (_) {}
      });
      pasteBufferRef.current = [];
      return [];
    });
  };

  useEffect(() => {
    pasteBufferRef.current = pasteBuffer;
  }, [pasteBuffer]);

  useEffect(() => {
    return () => {
      try {
        (pasteBufferRef.current || []).forEach((item) => {
          if (item.preview) URL.revokeObjectURL(item.preview);
        });
      } catch (_) {}
    };
  }, []);

  const addFilesToPasteBuffer = (files) => {
    const list = Array.from(files || []).filter((f) => f && /^image\//.test(f.type));
    if (!list.length) {
      message.warning('剪贴板中没有图片，请先用 Win+Shift+S 截图');
      return;
    }
    const items = list.map((file, i) => ({
      id: `${Date.now()}_${i}_${Math.random().toString(36).slice(2, 8)}`,
      file: new File([file], file.name || `screenshot_${Date.now()}_${i}.png`, { type: file.type || 'image/png' }),
      preview: URL.createObjectURL(file)
    }));
    setPasteBuffer((prev) => {
      const next = [...prev, ...items].slice(0, 10);
      message.success(`已粘贴 ${items.length} 张，当前暂存 ${Math.min(prev.length + items.length, 10)} 张（最多10张）`);
      return next;
    });
  };

  const handlePasteFromClipboardEvent = (e) => {
    const items = e.clipboardData?.items;
    if (!items || !items.length) return;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.type && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (!files.length) return;
    e.preventDefault();
    addFilesToPasteBuffer(files);
  };

  const handleReadClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.read) {
        const items = await navigator.clipboard.read();
        const files = [];
        for (const item of items) {
          const type = (item.types || []).find((t) => t.startsWith('image/'));
          if (!type) continue;
          const blob = await item.getType(type);
          files.push(new File([blob], `screenshot_${Date.now()}.png`, { type: blob.type || 'image/png' }));
        }
        if (files.length) {
          addFilesToPasteBuffer(files);
          return;
        }
      }
      message.info('请先截图，再按 Ctrl+V 或 Shift+Insert 粘贴到下方虚线框，也可点「粘贴截图」');
      try { pasteZoneRef.current?.focus(); } catch (_) {}
    } catch (err) {
      // 权限被拒或不支持时，引导用户用快捷键
      message.info('请点击下方虚线框后按 Ctrl+V / Shift+Insert 粘贴（Win+Shift+S 截图）');
      try { pasteZoneRef.current?.focus(); } catch (_) {}
    }
  };

  /** 将暂存截图：单张直接上传；多张水平拼接后上传 */
  const uploadPasteBuffer = async () => {
    if (!pasteBuffer.length) {
      message.warning('暂无截图，请先粘贴');
      return;
    }
    setPasteUploading(true);
    try {
      const files = pasteBuffer.map((x) => x.file);
      if (files.length === 1) {
        const modifiedFile = await addRandomElementsToImage(files[0]);
        await handleMultiUpload({
          file: modifiedFile,
          onSuccess: () => message.success('截图已上传'),
          onError: () => message.error('截图上传失败')
        });
      } else {
        const bitmapsSettle = await Promise.allSettled(files.slice(0, 10).map((f) => createImageBitmap(f)));
        const bitmaps = bitmapsSettle.filter((x) => x.status === 'fulfilled').map((x) => x.value);
        if (!bitmaps.length) {
          message.error('截图读取失败');
          return;
        }
        const stitched = stitchImagesLikePython(bitmaps, 'horizontal');
        if (!stitched) {
          message.error('拼接失败');
          return;
        }
        const blob = await fetch(stitched).then((r) => r.blob());
        const stitchedFile = new File(
          [blob],
          `stitched_paste_${Date.now()}.jpg`,
          { type: 'image/jpeg' }
        );
        await handleMultiUpload({
          file: stitchedFile,
          onSuccess: () => message.success(`已合并 ${bitmaps.length} 张截图并上传`),
          onError: () => message.error('上传失败')
        });
      }
      clearPasteBuffer();
    } catch (e) {
      console.error(e);
      message.error('处理截图失败');
    } finally {
      setPasteUploading(false);
    }
  };

  const handleMultiUpload = async ({ file, onSuccess, onError }) => {
    try {
      const formData = new FormData();
      let raw = (file && file.originFileObj) ? file.originFileObj : (file && file.file) ? file.file : file;
      // 压缩策略：大于1MB时先压缩，保持高质量（移动端和电脑端统一）
      if (raw && raw.size && raw.size > 1024 * 1024) {
        raw = await resizeImageFile(raw, 1200, 0.92);
      }
      formData.append('images', raw, raw?.name || 'image.png');
      // 统一使用ImgLoc图床，确保所有图片都有直链访问
      formData.append('mode', 'imagebed');
      formData.append('provider', selectedImageBed);
      console.log('上传参数:', { mode: 'imagebed', provider: selectedImageBed, fileSize: raw.size });
      console.log('FormData内容:', Array.from(formData.entries()));
      const res = await uploadAccountImages(formData);
      console.log('上传响应:', res);
      console.log('响应中的图片URLs:', res.images?.map(img => ({ url: img.url, data: img.data })));
      if (res.images && res.images.length) {
        const urls = res.images.map(img => img.url || img.data);
        setImageUrls(prev => Array.from(new Set([...(prev || []), ...urls])));
        // 不在这里显示成功消息，让调用方决定
      } else if (res.failed && res.failed.length > 0) {
        const failedReasons = res.failed.map(f => f.reason).join('; ');
        message.error(`图片上传失败: ${failedReasons}`);
      } else {
        message.error('图片上传失败，请重试');
      }
      onSuccess && onSuccess(res, file);
    } catch (e) {
      console.error('多图上传失败', e);
      const serverMsg = e?.response?.data?.error || e?.message || '图片上传失败';
      message.error(serverMsg);
      onError && onError(e);
    }
  };

  const handleUpload = async ({ file, onSuccess, onError }) => {
    try {
      const formData = new FormData();
      const raw = (file && file.originFileObj) ? file.originFileObj : (file && file.file) ? file.file : file;
      formData.append('file', raw, raw?.name || 'image.png');
      const res = await uploadAccountImage(formData);
      setImageUrl(res.url || res.data);
      message.success('图片上传成功');
      onSuccess && onSuccess(res, file);
    } catch (e) {
      console.error('上传失败', e);
      message.error('图片上传失败');
      onError && onError(e);
    }
  };

  const handleCancel = () => {
    // 如果是在添加模式下创建了邮箱但未保存，立即删除邮箱
    if (!isEdit && createdEmailSlugRef.current) {
      const slugToDelete = createdEmailSlugRef.current;
      // 清除 ref，避免 cleanup 重复删除
      createdEmailSlugRef.current = null;
      // 异步删除，不阻塞关闭操作
      deleteEmailAccount(slugToDelete).catch(err => {
        // 404 或 not_found 错误是正常的（邮箱可能已被删除或创建失败），静默处理
        const errorMsg = err?.message || err?.toString() || '';
        const isNotFound = errorMsg.includes('not_found') || 
                         errorMsg.includes('404') || 
                         errorMsg.includes('不存在');
        if (!isNotFound) {
          // 只有非 404 错误才记录警告
          console.warn('取消时删除未保存的邮箱失败:', err);
        }
      });
    }
    
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      size="middle"
      onFinish={handleSubmit}
      initialValues={
        isEdit
          ? {}
          : {
              cost: 0,
              price: 0,
              email_domain: emailDomain,
              status: defaultStatus || 'pending'
            }
      }
    >
      <Row gutter={[8,8]}>
        <Col xs={24} lg={8}>
          <Card title="基本信息" size="small" bodyStyle={{ padding: 8 }} headStyle={{ padding: '6px 10px' }}>
            <Form.Item
              name="username"
              label="用户名"
              rules={[{ required: true, message: '请输入用户名' }]}
              style={{ marginBottom: 8 }}
            >
              <Input placeholder="请输入TikTok用户名" />
            </Form.Item>

            <Row gutter={8}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="country"
                  label="账号地区"
                  style={{ marginBottom: 8 }}
                >
                  <Select
                    allowClear
                    placeholder="请选择地区"
                    options={COUNTRY_OPTIONS.map(c => ({ label: c, value: c }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="followers"
                  label="粉丝数"
                  style={{ marginBottom: 8 }}
                >
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="粉丝数" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="region_ratio"
                  label="地区占比"
                  style={{ marginBottom: 8 }}
                >
                  <Input placeholder="例如：US 40% / CN 30% / Other 30%" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="email"
              label="邮箱"
              rules={[
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
              style={{ marginBottom: 8 }}
            >
              <Input 
                placeholder="请输入邮箱地址" 
                suffix={
                  <Space size="small">
                    {(() => {
                      const v = form.getFieldValue('email');
                      if (!v || typeof v !== 'string' || !v.includes('@')) return false;
                      const d = String(v.split('@')[1] || '').trim().toLowerCase();
                      return EMAIL_DOMAINS.includes(d);
                    })() && (
                      <Button 
                        type="link" 
                        size="small" 
                        danger
                        loading={deletingEmail}
                        onClick={handleDeleteEmail}
                        style={{ padding: 0, height: 'auto' }}
                        title="删除邮箱"
                      >
                        🗑️
                      </Button>
                    )}
                    <Button 
                      type="link" 
                      size="small" 
                      loading={creatingEmail}
                      onClick={handleCreateEmail}
                      style={{ padding: 0, height: 'auto' }}
                      title="创建邮箱"
                    >
                      📧 创建邮箱
                    </Button>
                  </Space>
                }
              />
            </Form.Item>

            <Form.Item
              name="email_domain"
              label="邮箱域名"
              style={{ marginBottom: 8 }}
            >
              <Select
                value={emailDomain}
                onChange={(v) => {
                  setEmailDomain(v);
                  try { localStorage.setItem(EMAIL_DOMAIN_STORAGE_KEY, v); } catch (_) {}
                  try { form.setFieldsValue({ email_domain: v }); } catch (_) {}
                }}
                options={EMAIL_DOMAINS.map(d => ({ label: d, value: d }))}
              />
            </Form.Item>

            <Form.Item
              name="phone"
              label="手机号"
              style={{ marginBottom: 8 }}
            >
              <Input placeholder="请输入手机号" />
            </Form.Item>

            <Form.Item
              name="password"
              label="密码"
              style={{ marginBottom: 8 }}
            >
              <Input.Password 
                placeholder="请输入账号密码"
                suffix={
                  <Button 
                    type="link" 
                    size="small" 
                    onClick={handleGeneratePassword}
                    style={{ padding: 0, height: 'auto' }}
                    title="生成随机密码"
                  >
                    🎲 随机
                  </Button>
                }
              />
            </Form.Item>

            <Form.Item
              name="emailPassword"
              label="邮箱密码"
              style={{ marginBottom: 8 }}
            >
              <Input.Password placeholder="请输入邮箱密码" />
            </Form.Item>

            <Form.Item
              name="two_fa"
              label="2FA"
              style={{ marginBottom: 8 }}
              tooltip="填写后，复制账号信息时会附带 2FA 密钥与接码网址"
            >
              <Input.TextArea
                rows={2}
                placeholder="请输入 2FA 密钥（支持 otpauth 链接或纯密钥）"
                allowClear
              />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="分类和状态" size="small" bodyStyle={{ padding: 8 }} headStyle={{ padding: '6px 10px' }}>
            <Form.Item label="账号图片">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    可用 Win+Shift+S 截图后粘贴，无需先保存到电脑
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                />
                <Space wrap>
                  <Button icon={<UploadOutlined />} onClick={() => {
                  // 统一入口：选择本地并自动合并
                  try {
                    if (!fileInputRef.current) return;
                    const inputEl = fileInputRef.current;
                    const onChange = async (e) => {
                      try {
                        const files = Array.from(e.target.files || []).filter(f => /^image\//.test(f.type));
                        if (!files.length) return;
                        
                        // 单张图片：先添加随机元素，再上传到图床
                        if (files.length === 1) {
                          const originalFile = files[0];
                          const modifiedFile = await addRandomElementsToImage(originalFile);
                          
                          await handleMultiUpload({ 
                            file: modifiedFile, 
                            onSuccess: () => {
                              console.log('单张图片上传成功');
                              message.success('图片上传成功');
                            }, 
                            onError: (err) => {
                              console.error('单张图片上传失败:', err);
                              message.error('图片上传失败');
                            } 
                          });
                          inputEl.value = '';
                          return;
                        }
                        
                        // 多张图片：按 Python 默认「水平一排」合并，再上传到图床
                        // 拼接前不预压缩，保留清晰度
                        const pick = files.slice(0, 10);
                        const bitmapsSettle = await Promise.allSettled(pick.map(f => createImageBitmap(f)));
                        const bitmaps = bitmapsSettle.filter(x => x.status === 'fulfilled').map(x => x.value);
                        if (!bitmaps.length) { message.error('本地拼图失败'); return; }

                        const timestamp = Date.now();
                        const randomSeed = Math.floor(Math.random() * 10000);
                        // 与 image_stitcher.py 默认 horizontal 一致：4 张也是一排，不是 2x2
                        const stitched = stitchImagesLikePython(bitmaps, 'horizontal');
                        if (!stitched) { message.error('本地拼图失败'); return; }

                        // 轻微扰动，避免图床 MD5 去重（不影响观感）
                        try {
                          const img = await new Promise((resolve, reject) => {
                            const el = new window.Image();
                            el.onload = () => resolve(el);
                            el.onerror = reject;
                            el.src = stitched;
                          });
                          const c = document.createElement('canvas');
                          c.width = img.width;
                          c.height = img.height;
                          const cx = c.getContext('2d');
                          cx.drawImage(img, 0, 0);
                          const n = Math.floor(Math.random() * 3) + 1;
                          for (let p = 0; p < n; p++) {
                            cx.fillStyle = `hsl(${Math.floor(Math.random() * 360)}, 0%, ${Math.floor(Math.random() * 10) + 95}%)`;
                            cx.fillRect(Math.floor(Math.random() * c.width), Math.floor(Math.random() * c.height), 1, 1);
                          }
                          const uniqueStitched = c.toDataURL('image/jpeg', STITCH_JPEG_QUALITY);
                          const blob = await fetch(uniqueStitched).then(r => r.blob());
                          const stitchedFile = new File([blob], `stitched_${timestamp}_${randomSeed}_${Math.floor(Math.random() * 1000)}.jpg`, { type: 'image/jpeg' });

                          console.log('拼图生成完成，准备上传:', {
                            fileName: stitchedFile.name,
                            fileSize: stitchedFile.size,
                            fileType: stitchedFile.type,
                            layout: `horizontal x${bitmaps.length}`
                          });

                          await handleMultiUpload({
                            file: stitchedFile,
                            onSuccess: () => {
                              console.log('拼图上传成功');
                              message.success('拼图已生成并上传成功');
                            },
                            onError: (err) => {
                              console.error('拼图上传失败:', err);
                              message.error('拼图上传失败');
                            }
                          });
                        } catch (err) {
                          console.error('拼图处理失败:', err);
                          message.error('拼图处理失败');
                        }

                        inputEl.value = '';
                      } finally {
                        inputEl.removeEventListener('change', onChange);
                      }
                    };
                    inputEl.addEventListener('change', onChange);
                    inputEl.click();
                  } catch (_) { message.error('无法打开本地选择器'); }
                }}>
                  选择图片并自动合并上传
                </Button>
                  <Button icon={<SnippetsOutlined />} onClick={handleReadClipboard}>
                    粘贴截图
                  </Button>
                </Space>

                <div
                  ref={pasteZoneRef}
                  tabIndex={0}
                  onPaste={handlePasteFromClipboardEvent}
                  onClick={() => { try { pasteZoneRef.current?.focus(); } catch (_) {} }}
                  style={{
                    border: '1px dashed #91caff',
                    borderRadius: 8,
                    padding: 12,
                    background: '#f5faff',
                    outline: 'none',
                    cursor: 'text'
                  }}
                >
                  <div style={{ fontSize: 12, color: '#1677ff', marginBottom: 8 }}>
                    粘贴方式：Ctrl+V / Shift+Insert，或点上方「粘贴截图」（可连续多张再合并上传）
                  </div>
                  {pasteBuffer.length > 0 ? (
                    <>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                        {pasteBuffer.map((item) => (
                          <div key={item.id} style={{ position: 'relative' }}>
                            <img
                              src={item.preview}
                              alt="paste"
                              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                            />
                            <Button
                              size="small"
                              danger
                              style={{ position: 'absolute', top: -8, right: -8 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPasteBuffer((prev) => {
                                  const target = prev.find((x) => x.id === item.id);
                                  if (target?.preview) {
                                    try { URL.revokeObjectURL(target.preview); } catch (_) {}
                                  }
                                  return prev.filter((x) => x.id !== item.id);
                                });
                              }}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Space wrap>
                        <Button type="primary" loading={pasteUploading} onClick={uploadPasteBuffer}>
                          {pasteBuffer.length === 1 ? '上传这张截图' : `合并 ${pasteBuffer.length} 张并上传`}
                        </Button>
                        <Button icon={<ClearOutlined />} onClick={clearPasteBuffer} disabled={pasteUploading}>
                          清空暂存
                        </Button>
                      </Space>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                      暂无粘贴图片。流程：Win+Shift+S 截图 → Ctrl+V 或 Shift+Insert（或点「粘贴截图」）→ 可再截再粘 → 合并上传
                    </div>
                  )}
                </div>

                <div style={{ display: 'none' }} />
                <div style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: isMobile ? 'nowrap' : 'wrap',
                  alignItems: 'center',
                  overflowX: isMobile ? 'auto' : 'visible',
                  paddingBottom: isMobile ? 6 : 0
                }}>
                  {(imageUrls || []).map((u, idx) => (
                    <div key={`${u}-${idx}`} style={{ position: 'relative', flex: '0 0 auto' }}>
                      <Image src={u} width={isMobile ? 80 : 60} height={isMobile ? 80 : 60} style={{ objectFit: 'cover', borderRadius: 4 }} />
                      <Button
                        size="small"
                        danger
                        style={{ position: 'absolute', top: -8, right: -8 }}
                        onClick={() => {
                          // 删除逻辑：从多图数组里去掉该项
                          setImageUrls(prev => prev.filter((x) => x !== u));
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </Form.Item>
            <Row gutter={8}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="window_status"
                  label="橱窗状态"
                  tooltip="是否开通橱窗"
                >
                  <Select
                    onChange={(v) => {
                      if (v !== '已开') {
                        form.setFieldsValue({ window_opened_at: null });
                      }
                    }}
                  >
                    <Option value="已开">已开</Option>
                    <Option value="未开">未开</Option>
                    <Option value="掉窗">掉窗</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.window_status !== cur.window_status}>
                  {({ getFieldValue }) => (
                    getFieldValue('window_status') === '已开' ? (
                      <Form.Item name="window_opened_at" label="开通橱窗日期" style={{ marginBottom: 0 }}>
                        <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                      </Form.Item>
                    ) : null
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={8}>
              <Col xs={24} md={12}>
            <Form.Item
              name="status"
              label="状态"
              rules={[{ required: true, message: '请选择状态' }]}
            >
              <Select placeholder="请选择状态">
                <Option value="white">白号</Option>
                <Option value="pending">待售</Option>
                <Option value="suspended">封禁</Option>
                <Option value="banned">起号</Option>
                <Option value="sold">已售</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="api_key"
              label="API"
              style={{ marginBottom: 8 }}
            >
              <Input placeholder="请输入API密钥" />
            </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="category"
                  label="分类"
                >
                  <Select 
                    placeholder="请选择分类"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {accountTypes.map(type => (
                      <Option key={type.id} value={type.name}>
                        {type.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            

            <Form.Item
              name="description"
              label="描述"
            >
              <TextArea
                rows={3}
                placeholder="请输入账号描述"
              />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="价格信息" size="small" bodyStyle={{ padding: 8 }} headStyle={{ padding: '6px 10px' }}>
            <Form.Item
              name="price"
              label="售价"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="售价（默认 0）"
                min={0}
                precision={2}
                formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/¥\s?|(,*)/g, '')}
              />
            </Form.Item>

            <Form.Item
              name="cost"
              label="成本"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="成本"
                min={0}
                precision={2}
                formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/¥\s?|(,*)/g, '')}
              />
            </Form.Item>

            <Form.Item
              name="profit"
              label="利润"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="利润（自动计算）"
                disabled
                precision={2}
                formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/¥\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Card>
        </Col>
      </Row>

      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Space>
          <Button onClick={handleCancel}>
            取消
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isEdit ? '更新' : '创建'}
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export default AccountForm;